import { CHAT_KINDS, CHAT_PRICE_BANDS, CHAT_RADII_M } from '@citiguide/shared';
import type { ChatKind, ChatPriceBand, ChatRadiusM, CuratedState } from '@citiguide/shared';

export const MAX_PINS = 20;
export const MAX_PLAN_NAME = 60;

export interface URLParams {
  markerId?: string;
  language?: string;
  highlight?: boolean;
  accountId?: string;
  /** Category filter ids from `?cat=a,b` */
  categories: string[];
  /** Audience filter ids from `?aud=x,y` */
  audience: string[];
  /** `?price=free|cheap` */
  price: ChatPriceBand | null;
  /** `?kind=place|event` */
  kind: ChatKind | null;
  /** `?near=<radius in metres>`; the radius only, coordinates are never in the URL */
  nearRadius: ChatRadiusM | null;
  /** Pinned marker ids of an open / shared plan: `?pins=a,b` (max 20) */
  pins: string[];
  /** Name of the open / shared plan: `?pn=...` (trimmed, max 60 chars) */
  planName?: string;
}

/** Same id shape the public API accepts (see public-backend parseListParam). */
export const ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/i;

const parseIdList = (value: string | null): string[] => {
  if (!value) return [];
  const ids = new Set<string>();
  for (const piece of value.split(',')) {
    const id = piece.trim();
    if (id && ID_PATTERN.test(id)) ids.add(id);
  }
  return [...ids];
};

const parsePrice = (value: string | null): ChatPriceBand | null =>
  (CHAT_PRICE_BANDS as readonly string[]).includes(value ?? '') ? (value as ChatPriceBand) : null;

const parseKind = (value: string | null): ChatKind | null =>
  (CHAT_KINDS as readonly string[]).includes(value ?? '') ? (value as ChatKind) : null;

const parseRadius = (value: string | null): ChatRadiusM | null => {
  if (!value || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return (CHAT_RADII_M as readonly number[]).includes(n) ? (n as ChatRadiusM) : null;
};

const parsePlanName = (value: string | null): string | undefined => {
  const name = (value ?? '').trim().slice(0, MAX_PLAN_NAME);
  return name || undefined;
};

export const getURLParams = (): URLParams => {
  const params = new URLSearchParams(window.location.search);
  return {
    markerId: params.get('m') || undefined,
    language: params.get('l') || undefined,
    highlight: params.get('h') === '1',
    accountId: params.get('accountId') || params.get('a') || params.get('customerId') || params.get('c') || undefined,
    categories: parseIdList(params.get('cat')),
    audience: parseIdList(params.get('aud')),
    price: parsePrice(params.get('price')),
    kind: parseKind(params.get('kind')),
    nearRadius: parseRadius(params.get('near')),
    pins: parseIdList(params.get('pins')).slice(0, MAX_PINS),
    planName: parsePlanName(params.get('pn')),
  };
};

export const setURLParam = (key: string, value: string | null) => {
  const url = new URL(window.location.href);
  if (value === null) {
    url.searchParams.delete(key);
  } else {
    url.searchParams.set(key, value);
  }
  window.history.replaceState({}, '', url.toString());
};

/**
 * Mirror the curated filter state to the URL (`cat` / `aud` / `price` / `kind` / `near`, each removed
 * when empty) so a filtered view is shareable. Note `c` and `a` are already account params.
 * `near` carries the radius only: the visitor's coordinates are never written to the URL.
 */
export const writeCuratedStateToURL = (state: CuratedState) => {
  setURLParam('cat', state.categories.length > 0 ? state.categories.join(',') : null);
  setURLParam('aud', state.audience.length > 0 ? state.audience.join(',') : null);
  setURLParam('price', state.price);
  setURLParam('kind', state.kind);
  setURLParam('near', state.near ? String(state.near.radius_m) : null);
};

/** Mirror an open plan (`pins` / `pn`); pass null to remove both. */
export const writeOpenPlanToURL = (plan: { name: string; pinnedMarkerIds: string[] } | null) => {
  const pins = plan ? plan.pinnedMarkerIds.filter((id) => ID_PATTERN.test(id)).slice(0, MAX_PINS) : [];
  setURLParam('pins', pins.length > 0 ? pins.join(',') : null);
  setURLParam('pn', plan && plan.name.trim() ? plan.name.trim().slice(0, MAX_PLAN_NAME) : null);
};
